'use client';

import Header from '@/app/components/common/Header';
import PageLayout from '@/app/components/common/PageLayout';
import { LAST_UPDATED } from '@/lib/legal/business';
import type { LegalSection } from '@/lib/legal/business';
import { motion } from 'framer-motion';
import Link from 'next/link';

type LegalDocumentViewProps = {
  title: string;
  subtitle: string;
  intro: string;
  sections: LegalSection[];
  noticeTitle: string;
  noticeParagraphs: string[];
  ctaTitle: string;
  ctaSubtitle: string;
  contactEmail: string;
  crossLink: { href: string; label: string };
};

export default function LegalDocumentView({
  title,
  subtitle,
  intro,
  sections,
  noticeTitle,
  noticeParagraphs,
  ctaTitle,
  ctaSubtitle,
  contactEmail,
  crossLink,
}: LegalDocumentViewProps) {
  return (
    <PageLayout>
      <section className="relative min-h-[50vh] flex items-center bg-primary text-primary-foreground">
        <Header />
        <div className="container mx-auto px-6 py-20 mt-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">{title}</h1>
            <p className="text-xl opacity-90">{subtitle}</p>
            <p className="text-sm opacity-75 mt-4">Last Updated: {LAST_UPDATED}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-card rounded-3xl p-8 md:p-12 shadow-lg border border-border mb-8"
            >
              <p className="text-lg text-muted-foreground leading-relaxed">{intro}</p>
            </motion.div>

            <div className="space-y-8">
              {sections.map((section, index) => (
                <motion.div
                  key={section.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  className="bg-card rounded-3xl p-8 shadow-lg border border-border"
                >
                  <h2 className="text-3xl font-bold text-foreground mb-6">{section.title}</h2>
                  <div className="space-y-3">
                    {section.content.map((item, itemIndex) => (
                      <p
                        key={itemIndex}
                        className={`${
                          item.startsWith('•') ? 'pl-4' : ''
                        } text-muted-foreground leading-relaxed`}
                      >
                        {item}
                      </p>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mt-12 bg-secondary rounded-3xl p-8 border border-border"
            >
              <h3 className="text-2xl font-bold text-foreground mb-4">{noticeTitle}</h3>
              {noticeParagraphs.map((paragraph, index) => (
                <p
                  key={index}
                  className={`text-muted-foreground leading-relaxed${index < noticeParagraphs.length - 1 ? ' mb-4' : ''}`}
                >
                  {paragraph}
                </p>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-bold mb-6">{ctaTitle}</h2>
            <p className="text-lg mb-8 opacity-90">{ctaSubtitle}</p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <motion.a
                href={`mailto:${contactEmail}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-primary hover:bg-white/90 px-8 py-4 rounded-xl font-semibold shadow-lg transition-all"
              >
                Contact Us
              </motion.a>
              <Link
                href={crossLink.href}
                className="text-white hover:underline px-8 py-4 font-semibold"
              >
                {crossLink.label}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </PageLayout>
  );
}
